import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  LucideIcon,
  Cloud,
  BarChart,
  GitBranch,
  Database,
  Shield,
  Clock,
  Terminal,
  Scale,
  Container,
  Users,
  Filter,
} from 'lucide-react';
import SEO from '../components/SEO';
import { Section } from '../components/design-system';
import { Hero } from '../components/hero';
import { CtaBlock, ServiceCard } from '../components/cards';
import { setPageTitle, PAGE_TITLES } from '../utils/pageTitle';

export interface Service {
  id: string;
  icon: LucideIcon;
  title: string;
  shortTitle: string;
  description: string;
  features: string[];
  category: string;
}

export const serviceCategories = [
  { id: 'all', name: 'All Services', icon: Filter },
  { id: 'cloud', name: 'Cloud & Infrastructure', icon: Cloud },
  { id: 'automation', name: 'Automation & DevOps', icon: GitBranch },
  { id: 'reliability', name: 'Reliability & SRE', icon: Scale },
];

export const services: Service[] = [
  {
    id: 'cloud',
    icon: Cloud,
    title: 'Cloud Architecture',
    shortTitle: 'Cloud',
    description: 'Design and implement scalable infrastructure with AWS, GCP, or Azure expertise.',
    category: 'cloud',
    features: [
      'Infrastructure as Code (Terraform, CloudFormation)',
      'Microservices Architecture',
      'Container Orchestration (Kubernetes)',
      'Cloud Cost Optimization',
    ],
  },
  {
    id: 'kubernetes',
    icon: Container,
    title: 'Kubernetes & Cloud Native',
    shortTitle: 'Kubernetes',
    description: 'Build and operate scalable, resilient applications with Kubernetes and cloud-native technologies.',
    category: 'cloud',
    features: [
      'Production-grade Kubernetes Architecture',
      'Service Mesh & Network Security',
      'Helm Charts & Package Management',
      'Auto-scaling & Resource Optimization',
      'Observability & Monitoring Setup',
    ],
  },
  {
    id: 'automation',
    icon: Terminal,
    title: 'Infrastructure Automation',
    shortTitle: 'Automation',
    description: 'Automate routine tasks and infrastructure management for improved efficiency.',
    category: 'automation',
    features: [
      'Configuration Management',
      'Infrastructure Provisioning',
      'Backup and Recovery',
      'Patch Management',
    ],
  },
  {
    id: 'cicd',
    icon: GitBranch,
    title: 'CI/CD Excellence',
    shortTitle: 'CI/CD',
    description: 'Automate deployments and ensure consistent delivery with modern practices.',
    category: 'automation',
    features: [
      'Automated Build and Deploy Pipelines',
      'Container Image Management',
      'Release Strategy Design',
      'Quality Gates Implementation',
    ],
  },
  {
    id: 'database',
    icon: Database,
    title: 'Database Operations',
    shortTitle: 'Database',
    description: 'Optimize performance and ensure high availability for your data layer.',
    category: 'cloud',
    features: ['High Availability Setup', 'Performance Tuning', 'Backup Strategies', 'Data Migration'],
  },
  {
    id: 'security',
    icon: Shield,
    title: 'Security First',
    shortTitle: 'Security',
    description: 'Implement zero-trust architecture and compliance best practices.',
    category: 'cloud',
    features: [
      'Security Scanning and Remediation',
      'Access Management (IAM)',
      'Secrets Management',
      'Compliance Automation',
    ],
  },
  {
    id: 'observability',
    icon: BarChart,
    title: 'Observability',
    shortTitle: 'Observability',
    description: 'Implement comprehensive observability solutions with metrics, logs, and traces for deep system insights.',
    category: 'reliability',
    features: [
      'Full-Stack Observability Implementation',
      'Advanced Metrics & Performance Analytics',
      'Distributed Tracing & Service Maps',
      'Intelligent Alert Management & SLOs',
      'Real-time Dashboards & Business KPIs',
    ],
  },
  {
    id: 'sre',
    icon: Scale,
    title: 'SRE Implementation',
    shortTitle: 'SRE',
    description: 'Implement Site Reliability Engineering practices in your organization.',
    category: 'reliability',
    features: [
      'Error Budgets',
      'SLO/SLI Definition',
      'Reliability Improvement Programs',
      'Team Structure Design',
    ],
  },
  {
    id: 'incident',
    icon: Clock,
    title: 'Incident Response',
    shortTitle: 'Incident',
    description: '24/7 support with defined SLAs and rapid resolution times.',
    category: 'reliability',
    features: [
      'Incident Response Planning',
      'Runbook Development',
      'Post-mortem Analysis',
      'On-call Process Design',
    ],
  },
  {
    id: 'devops-interview',
    icon: Users,
    title: 'DevOps/SRE Interview Service',
    shortTitle: 'Interview',
    description: 'Expert assistance in interviewing and selecting top DevOps and SRE talent for your organization.',
    category: 'automation',
    features: [
      'Technical Assessment Design',
      'Candidate Screening & Evaluation',
      'Role-specific Interview Frameworks',
      'Skill Gap Analysis',
      'Hiring Process Optimization',
    ],
  },
];

const Services: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('category') || 'all';
  const activeCategory = serviceCategories.some(c => c.id === categoryParam) ? categoryParam : 'all';

  useEffect(() => {
    setPageTitle(PAGE_TITLES.SERVICES);
  }, []);

  const countsByCategory = useMemo(() => {
    const counts: Record<string, number> = { all: services.length };
    services.forEach(s => {
      counts[s.category] = (counts[s.category] || 0) + 1;
    });
    return counts;
  }, []);

  const setCategory = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === 'all') next.delete('category');
    else next.set('category', id);
    setSearchParams(next, { replace: true });
  };

  const filtered =
    activeCategory === 'all' ? services : services.filter(s => s.category === activeCategory);

  return (
    <>
      <SEO
        title="Expert SRE & DevOps Solutions | ConsultSRE"
        description="Comprehensive SRE and DevOps solutions including Cloud Architecture, Kubernetes, Infrastructure Automation, CI/CD, and Database Operations."
        keywords="cloud architecture, kubernetes, infrastructure automation, CI/CD, database operations, DevOps, SRE consulting"
        type="website"
      />

      <Hero
        eyebrow="services · catalog"
        title={<>Most consultants leave when the engagement ends. We leave when <em>your system can run without&nbsp;us</em>.</>}
        lede="Ten disciplines. Every engagement is scoped, fixed-price where it can be, and leaves your team owning the outcome."
      />
      <section className="ds-section" style={{ paddingTop: 0, paddingBottom: 48 }}>
        <div className="ds-wrap">

          <div
            style={{
              display: 'flex',
              gap: 6,
              marginTop: 32,
              flexWrap: 'wrap',
              borderTop: '1px dashed var(--line-strong)',
              paddingTop: 24,
            }}
          >
            {serviceCategories.map(cat => {
              const Icon = cat.icon;
              const active = activeCategory === cat.id;
              const count = countsByCategory[cat.id] ?? 0;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setCategory(cat.id)}
                  aria-pressed={active}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 14px',
                    background: active ? 'var(--accent-dim)' : 'var(--bg-elev)',
                    color: active ? 'var(--accent-ink)' : 'var(--ink-dim)',
                    border: `1px solid ${active ? 'var(--accent-dim)' : 'var(--line)'}`,
                    borderRadius: 'var(--r-sm)',
                    fontSize: 13,
                    fontFamily: 'var(--font-body)',
                    cursor: 'pointer',
                  }}
                >
                  <Icon size={14} strokeWidth={1.5} />
                  {cat.name}
                  <span
                    style={{
                      marginLeft: 4,
                      fontFamily: 'var(--font-mono)',
                      fontSize: 11,
                      color: active ? 'var(--accent-ink)' : 'var(--ink-mute)',
                    }}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <Section tight>
        <div className="services-grid">
          {filtered.map(svc => {
            const Icon = svc.icon;
            return (
              <ServiceCard
                key={svc.id}
                to={`/services/${svc.id}`}
                title={svc.title}
                description={svc.description}
                tags={svc.features.slice(0, 4)}
                icon={<Icon size={20} strokeWidth={1.25} />}
              />
            );
          })}
        </div>
      </Section>

      <Section>
        <CtaBlock
          title="Ready to enhance your infrastructure?"
          description="Tell us about the service you care about most."
          primaryCta={{
            label: 'Book an assessment',
            to: '/contact',
          }}
          secondaryCta={{ label: 'See methodology', to: '/#services' }}
        />
      </Section>

    </>
  );
};

export default Services;
